import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import { useStyles } from "./RCALibraryDetail.styles";
import MainTransparentBackButton from "components/MainTransparentBackButton";
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Divider,
    IconButton,
    InputLabel,
    LinearProgress,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import MainCardButtonAction from "components/MainCardButtonAction";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import UploadDropZone from "components/UploadDropZone/UploadDropZone.view";
import RCAUploadZone from "pages/Corporate/RCA/components/RCAUploadZone";
import { RCAUploadTypeEnum } from "pages/Corporate/RCA/components/RCAUploadZone/RCAUploadZoneInterfaces";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { createRef, useContext, useMemo, useState, useRef, useEffect } from "react";
import { DropzoneRef } from "react-dropzone";
import {
    CORPORATE_BACKOFFICE_PATHS,
    DefaultTranslationFieldsInTable,
    RCA_STATUSES,
    RcaFrequencyTriggerUnitEnum,
    RcaTypeEnum,
    allFileTypeExtensions,
    documentsFileTypeExtensions,
    documentsFileTypeExtensionsRegExp,
    mediaFileTypeExtensions,
    mediaFileTypeExtensionsRegExp,
} from "constants/constants";
import { useTranslation } from "react-i18next";
import RCAStatusDropdown from "pages/Corporate/RCA/components/RCAStatusDropdown";
import { IRCALibraryDetailFormValues } from "./RCALibraryDetailInterfaces";
import { Form, Formik } from "formik";
import { addRCALibraryFormValidationSchema } from "validations/schemas";
import dayjs from "dayjs";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import RCAObservationItem from "pages/Corporate/RCA/components/RCAObservationItem";
import RCAUserWithAvatar from "pages/Corporate/RCA/components/RCAUserWithAvatar";
import AddRCAMembersModal from "./components/AddRCAMembersModal";
import { IUser } from "entities/Users/UsersEntityInterfaces";
import useRolesPermissions from "hooks/useRolesPermissions";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import DeleteIcon from "@mui/icons-material/Delete";
import RCA5WhysAnalisys from "./components/RCA5WhysAnalisys";
import { I5WhyAnalisys, IWhy } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { uploadFiveWhyAnalysisFiles, uploadRcaFiles } from "api/queries/RCA/RCALibraryAPI";
import RCAFisboneDiagram from "./components/RCAFisboneDiagram";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import RCAAddObservationModal from "pages/Corporate/RCA/components/RCAAddObservationModal/RCAAddObservationModal.view";
import useDateFormat from "hooks/useDateFormat";
import { DocumentPreviewer } from "components/DocumentPreviewer/DocumentPreviewer.view";
import { RCAMediaPreviewer } from "pages/Corporate/RCA/components/RCAMediaPreviewer/RCAMediaPreviewer";
import FrequencyCumulativeLengthTooltip from "./components/FrequencyCumulativeLengthTooltip/FrequencyCumulativeLengthTooltip";
import moment from "moment";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IFailureCode } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import { RCAElipsisMenu } from "pages/Corporate/RCA/components/RCAElipsisMenu/RCAElipsisMenu";
import { IOption } from "pages/Corporate/RCA/components/RCAElipsisMenu/RCAElipsisMenuInterfaces";
import CreateEditManualRCAModal from "./components/CreateEditManualRCAModal/CreateEditManualRCAModal";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";
import MainModal from "components/MainModal";
import { RCALibraryHistoryLogs } from "./components/RCALibraryHistoryLogs";
import { UserContext } from "contexts/UserContext/UserContext";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import ActionPlansTable from "pages/Corporate/RCA/components/ActionPlansTable/ActionPlansTable.view";

const RCALibraryDetail = () => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { rcaId } = useParams();
    const dropzoneDocumentsRef = createRef<DropzoneRef>();
    const dropzoneMediaRef = createRef<DropzoneRef>();
    const dropzoneFiveWhyAnalysisRef = createRef<DropzoneRef>();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSaving, setIsSaving] = useState(false);
    const { t, i18n } = useTranslation();
    const { formatDate, getDateFormatByLanguage } = useDateFormat();
    const { user_id } = useContext(UserContext);
    const { apolloQueryError, rca, loading, refetch } = RCALibraryEntity.GetRCA(Number(rcaId)!);
    const {
        rcaLogs,
        loading: loadingLogs,
        refetch: refetchLogs,
    } = RCALibraryEntity.GetRCAHistoricChangesLog(Number(rcaId)!);
    const { upsertSetRCAWithTeamMembers } = RCALibraryEntity.UpsertRCAWithTeamMembers();
    const { upsertSetRCA5WhyAnalysis } = RCALibraryEntity.UpsertRCA5WhyAnalysis();
    const { upsertRCA5WhyAnalysisItem } = RCALibraryEntity.UpsertRCA5WhyAnalysisItem();
    const { lastSevenDaysDowntime } = RCALibraryEntity.GetLastSevenDaysDowntimeLengthByType(
        rca?.rca_setting?.failure_type_id,
        rca?.rca_event_reports?.[0]?.event_report?.machine_center?.machine_center_id,
        rca?.rca_event_reports?.[0]?.event_report?.start_date,
    );
    const { updateRCAMediaMutation } = RCALibraryEntity.UpdateRCAMedia();
    const { updateFiveWhyAnalysisMedia } = RCALibraryEntity.UpdateFiveWhyAnalysisMedia();

    const rcaOwnershipIds = useMemo(
        () =>
            rca
                ? [
                      ...(rca?.owner?.user_id ? [rca.owner.user_id] : []),
                      ...(rca?.created_by ? [rca?.created_by] : []),
                      ...(rca?.rca_team_members ? rca?.rca_team_members?.map((rtm) => rtm.user.user_id) : []),
                  ]
                : [],
        [rca],
    );
    const { RCA_PERMISSIONS } = useRolesPermissions(rcaOwnershipIds);

    const [progress, setProgress] = useState(0);
    const [isUploadingFiles, setIsUploadingFiles] = useState<RCAUploadTypeEnum | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarFailureMessage, setSnackbarFailureMessage] = useState("");
    const [openRCAMembersModal, setOpenRCAMembersModal] = useState<boolean>(false);
    const [openRCAObservationModal, setOpenRCAObservationModal] = useState<boolean>(false);
    const [rcaMainDataChanged, setRcaMainDataChanged] = useState<boolean>(false);
    const [selectedRCATeamMembers, setSelectedRCATeamMembers] = useState<IUser[]>([]);
    const [isCreateRCAModalOpen, setIsCreateRCAModalOpen] = useState<boolean>(false);
    const [openRCADeleteModal, setOpenRCADeleteModal] = useState<boolean>(false);
    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
    const [fileDescription, setFileDescription] = useState("");
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isFiveWhysFileUploadModalOpen, setIsFiveWhysFileUploadModalOpen] = useState(false);
    const [fiveWhysFileDescription, setFiveWhysFileDescription] = useState("");
    const [pendingFiveWhysFiles, setPendingFiveWhysFiles] = useState<File[]>([]);
    const actionPlansTableRef = useRef<HTMLDivElement>(null);
    const hasScrolledToActionPlansRef = useRef<boolean>(false);

    const initFormValues: IRCALibraryDetailFormValues = {
        dueDate: new Date(rca?.due_date) || new Date(),
        ownerId: rca?.owner?.user_id || "",
        statusId: rca?.status?.status_id || RCA_STATUSES.NOT_STARTED,
        rcaTeamMembers: rca?.rca_team_members || [],
        meetingTypeId: rca?.meeting_type_id || "",
    };

    const rcaMembersListToShow = useMemo(() => {
        if (!rca?.rca_team_members) return [];
        const rcaMembersList = [...(rca.rca_team_members ? rca.rca_team_members : [])];
        if (selectedRCATeamMembers.length > 0) {
            selectedRCATeamMembers.forEach((user) => {
                if (!rcaMembersList.find((rcaMember) => rcaMember.user.user_id === user.user_id)) {
                    rcaMembersList.push({
                        rca_team_member_id: 0,
                        user,
                    });
                }
            });
        }
        return rcaMembersList;
    }, [rca, selectedRCATeamMembers]);

    const documentFiles = useMemo(() => {
        if (!rca?.rca_media) return [];
        return rca.rca_media.filter((file) => documentsFileTypeExtensionsRegExp.test(file.media.media_type));
    }, [rca]);

    const mediaFiles = useMemo(() => {
        if (!rca?.rca_media) return [];
        return rca.rca_media.filter((file) => mediaFileTypeExtensionsRegExp.test(file.media.media_type));
    }, [rca]);

    const functionalLocationNames = useMemo(() => {
        return (
            rca?.rca_event_reports?.reduce((uniqueNames: string[], event) => {
                const name = event?.event_report?.functional_location
                    ? handleNameTranslation(
                          i18n.language,
                          event?.event_report?.functional_location?.translation.translation_names,
                      )
                    : undefined;
                if (name && !uniqueNames.includes(name)) {
                    uniqueNames.push(name);
                }
                return uniqueNames;
            }, []) || []
        );
    }, [rca, i18n.language]);

    const failureCodeNames = useMemo(() => {
        return (
            rca?.rca_event_reports?.reduce((uniqueNames: string[], event) => {
                const name = translateDefaultEntity(
                    i18n.language,
                    event?.event_report?.failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                );
                if (name && !uniqueNames.includes(name)) {
                    uniqueNames.push(name);
                }
                return uniqueNames;
            }, []) || []
        );
    }, [rca, i18n.language]);

    const secondaryFailureCodeNames = useMemo(() => {
        return (
            rca?.rca_event_reports?.reduce((uniqueNames: string[], event) => {
                const name = translateDefaultEntity(
                    i18n.language,
                    event?.event_report?.secondary_failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                );
                if (name && !uniqueNames.includes(name)) {
                    uniqueNames.push(name);
                }
                return uniqueNames;
            }, []) || []
        );
    }, [rca, i18n.language]);

    const shiftNames = useMemo(() => {
        return (
            rca?.rca_event_reports?.reduce((uniqueNames: string[], event) => {
                const name = event?.event_report?.shift_day?.shift_historic?.shift?.name;
                if (name && !uniqueNames.includes(name)) {
                    uniqueNames.push(name);
                }
                return uniqueNames;
            }, []) || []
        );
    }, [rca]);

    const isRCAManual = useMemo(() => rca?.rca_type?.rca_type_id === RcaTypeEnum.MANUAL, [rca]);

    const rcaElipsisMenuOptions: IOption[] = useMemo(
        () => [
            {
                name: t("rca.rca_library.rca_detail.edit_rca"),
                callback: () => setIsCreateRCAModalOpen(true),
                disabled: !RCA_PERMISSIONS?.rca_library?.canEdit,
            },
            {
                name: t("rca.rca_library.rca_detail.delete_rca"),
                callback: () => setOpenRCADeleteModal(true),
                disabled: !RCA_PERMISSIONS?.rca_library?.canDelete,
            },
        ],
        [RCA_PERMISSIONS, t],
    );

    const rcaLength = useMemo(() => {
        let messageResult = "";

        if (rca?.rca_type?.rca_type_id === RcaTypeEnum.BY_DURATION) {
            const durationInMinutes = rca.rca_event_reports?.[0]
                ? Math.max(1, Math.floor(rca.rca_event_reports[0]?.event_report.valid_time / 60))
                : 0;

            if (rca.rca_event_reports?.[0]) {
                messageResult = `${durationInMinutes} `;

                if (durationInMinutes > 1) {
                    messageResult += t("rca.rca_library.rca_detail.length_unit_minute_plural");
                } else {
                    messageResult += t("rca.rca_library.rca_detail.length_unit_minute_singular");
                }
            } else {
                messageResult = "--";
            }
            return messageResult;
        } else if (rca?.rca_type?.rca_type_id === RcaTypeEnum.BY_FREQUENCY) {
            const occurrences = rca.rca_event_reports?.length;
            const totalDurationInSeconds = rca.rca_event_reports
                ? rca.rca_event_reports.reduce((total, event) => {
                      const eventDuration = event.event_report.valid_time;
                      return total + eventDuration;
                  }, 0)
                : 0;
            const totalDurationInMinutes = totalDurationInSeconds
                ? Math.max(1, Math.floor(totalDurationInSeconds / 60))
                : 0;

            let triggerUnit;
            if (
                RcaFrequencyTriggerUnitEnum.DAY === rca.rca_setting.rca_frequency_trigger.rca_frequency_trigger_unit_id
            ) {
                triggerUnit = t("rca.rca_library.rca_detail.length_unit_day");
            } else if (
                RcaFrequencyTriggerUnitEnum.SHIFT ===
                rca.rca_setting.rca_frequency_trigger.rca_frequency_trigger_unit_id
            ) {
                triggerUnit = t("rca.rca_library.rca_detail.length_unit_shift");
            } else if (
                RcaFrequencyTriggerUnitEnum.WEEK === rca.rca_setting.rca_frequency_trigger.rca_frequency_trigger_unit_id
            ) {
                triggerUnit = t("rca.rca_library.rca_detail.length_unit_week");
            } else {
                triggerUnit = t("rca.rca_library.rca_detail.length_unit_month");
            }

            if (occurrences) {
                let occurrenceText = "";
                if (occurrences > 1) {
                    occurrenceText = t("rca.rca_library.rca_detail.length_unit_occurrence_plural");
                } else {
                    occurrenceText = t("rca.rca_library.rca_detail.length_unit_occurrence_singular");
                }

                messageResult = `${occurrences} ${occurrenceText} ${triggerUnit}, \n${totalDurationInMinutes} ${t(
                    "rca.rca_library.rca_detail.total_minutes",
                )}`;
            } else {
                messageResult = "--";
            }

            return messageResult;
        }
    }, [rca, t]);

    const handleFileChange = async (files: File[], mediaType: RCAUploadTypeEnum) => {
        if (!files || (files && !files.length)) return;
        setIsUploadingFiles(mediaType);
        setPendingFiles(files);
        setIsFileUploadModalOpen(true);
    };

    const handleFileUpload = async () => {
        if (!pendingFiles.length) return;
        const fileExtension = pendingFiles[0].name.split(".").pop();
        const renamedFile = new File(
            [pendingFiles[0]],
            fileDescription ? `${fileDescription}.${fileExtension}` : pendingFiles[0].name,
            { type: pendingFiles[0].type },
        );
        const formData = new FormData();
        formData.set("file", renamedFile);
        formData.set("rca_id", rcaId!.toString() || "");
        formData.set("user_id", user_id!.toString() || "");

        try {
            await uploadRcaFiles(Number(rcaId), formData, (uploadEvt) => {
                let percentCompleted = Math.round((uploadEvt.loaded * 100) / (uploadEvt?.total || 1));
                setProgress(percentCompleted);
            });

            refetch();
            await refetchLogs();
            setProgress(0);
            setIsUploadingFiles(null);
            setSnackbarMessage(t("rca.issues.issue_detail.upload_success"));
        } catch (error) {
            setProgress(0);
            setIsUploadingFiles(null);
        }

        setFileDescription("");
        setIsFileUploadModalOpen(false);
        setPendingFiles([]);
    };

    const handleFileFiveWhyAnalysisChange = async (files: File[], mediaType: RCAUploadTypeEnum) => {
        if (!files || (files && !files.length)) return;
        setIsUploadingFiles(mediaType);
        setPendingFiveWhysFiles(files);
        setIsFiveWhysFileUploadModalOpen(true);
    };

    const handle5WhysFileUpload = async () => {
        let five_why_analysis_id = rca.five_why_analyses[0]?.five_why_analysis_id;

        if (!five_why_analysis_id) {
            const fiveWhyAnalysisResponse = await upsertSetRCA5WhyAnalysis({
                variables: {
                    rca: {
                        rca_id: rca.rca_id,
                    },
                    five_why_analysis: {
                        rca_id: rca.rca_id,
                        problem_statement: "",
                        root_cause_conclusion: "",
                    },
                    why: [],
                },
            });
            five_why_analysis_id =
                fiveWhyAnalysisResponse.data?.insert_five_why_analysis?.returning[0].five_why_analysis_id;
        }

        if (five_why_analysis_id) {
            const formData = new FormData();
            const fileExtension = pendingFiveWhysFiles[0].name.split(".").pop();
            const renamedFile = new File(
                [pendingFiveWhysFiles[0]],
                fiveWhysFileDescription ? `${fiveWhysFileDescription}.${fileExtension}` : pendingFiveWhysFiles[0].name,
                { type: pendingFiveWhysFiles[0].type },
            );
            formData.set("file", renamedFile);
            formData.set("five_why_analysis_id ", five_why_analysis_id.toString() || "");
            formData.set("user_id", user_id!.toString() || "");

            try {
                await uploadFiveWhyAnalysisFiles(Number(five_why_analysis_id), formData, (uploadEvt) => {
                    let percentCompleted = Math.round((uploadEvt.loaded * 100) / (uploadEvt?.total || 1));
                    setProgress(percentCompleted);
                });

                refetch();
                await refetchLogs();
                setProgress(0);
                setIsUploadingFiles(null);
                setSnackbarMessage(t("rca.issues.issue_detail.upload_success"));
            } catch (error) {
                setProgress(0);
                setIsUploadingFiles(null);
            }

            setFiveWhysFileDescription("");
            setIsFiveWhysFileUploadModalOpen(false);
            setPendingFiveWhysFiles([]);
        }
    };

    const handleSubmit = async (values: IRCALibraryDetailFormValues) => {
        setIsSaving(true);

        const rcaDetailValues = {
            due_date: values.dueDate.toISOString(),
            status_id: values.statusId,
            owner_id: values.ownerId,
            ...(values.meetingTypeId && { meeting_type_id: values.meetingTypeId }),
        };

        try {
            await upsertSetRCAWithTeamMembers({
                variables: {
                    rcaId: rca.rca_id,
                    rcaChanges: rcaDetailValues,
                    rca_team_members: rcaMembersListToShow.map((member) => ({
                        ...(member.rca_team_member_id && { rca_team_member_id: member.rca_team_member_id }),
                        rca_id: rca.rca_id,
                        user_id: member.user.user_id,
                    })),
                },
            });
            refetch();
            await refetchLogs();
            setIsSaving(false);
            setSnackbarMessage(t("rca.rca_library.rca_detail.update_success"));
            setRcaMainDataChanged(false);
        } catch {
            setIsSaving(false);
            return;
        }
    };

    const handleFiveWhysAnalysisSave = async (fiveWhyAnalisys: Partial<I5WhyAnalisys>) => {
        setIsSaving(true);
        try {
            if (fiveWhyAnalisys.why && fiveWhyAnalisys.why.length > 0) {
                const fiveWhyAnalysisResponse = await upsertSetRCA5WhyAnalysis({
                    variables: {
                        rca: {
                            rca_id: rca.rca_id,
                        },
                        five_why_analysis: {
                            ...(fiveWhyAnalisys.five_why_analysis_id && {
                                five_why_analysis_id: fiveWhyAnalisys.five_why_analysis_id,
                            }),
                            rca_id: rca.rca_id,
                            problem_statement: fiveWhyAnalisys.problem_statement,
                            root_cause_conclusion: fiveWhyAnalisys.root_cause_conclusion,
                        },
                        why: [],
                    },
                });

                const fiveWhyAnalysisId =
                    fiveWhyAnalysisResponse.data?.insert_five_why_analysis?.returning[0].five_why_analysis_id;
                if (fiveWhyAnalysisId) {
                    const currentQuestion = fiveWhyAnalisys.why.find((why) => !why.isAnswer && !why.why_id)!;
                    if (currentQuestion) {
                        const questionResponse = await upsertRCA5WhyAnalysisItem({
                            variables: {
                                why: {
                                    ...(currentQuestion.why_id && { why_id: currentQuestion.why_id }),
                                    ...currentQuestion,
                                    five_why_analysis_id: fiveWhyAnalysisId,
                                },
                            },
                        });

                        const questionId =
                            questionResponse.data?.insert_why?.returning[0]?.why_id || currentQuestion.why_id;

                        if (questionId) {
                            const currentAnswer = fiveWhyAnalisys.why.find(
                                (why) => why.isAnswer || why.primary_why_id === questionId,
                            )!;

                            delete currentAnswer.isAnswer;
                            await upsertRCA5WhyAnalysisItem({
                                variables: {
                                    why: {
                                        ...(currentAnswer.why_id && { why_id: currentAnswer.why_id }),
                                        ...currentAnswer,
                                        primary_why_id: questionId,
                                        five_why_analysis_id: fiveWhyAnalysisId,
                                    },
                                },
                            });
                        }
                    } else {
                        await upsertSetRCA5WhyAnalysis({
                            variables: {
                                rca: {
                                    rca_id: rca.rca_id,
                                },
                                five_why_analysis: {
                                    ...(fiveWhyAnalisys.five_why_analysis_id && {
                                        five_why_analysis_id: fiveWhyAnalisys.five_why_analysis_id,
                                    }),
                                    rca_id: rca.rca_id,
                                    problem_statement: fiveWhyAnalisys.problem_statement,
                                    root_cause_conclusion: fiveWhyAnalisys.root_cause_conclusion,
                                },
                                why: (fiveWhyAnalisys.why || []).map((why) => ({
                                    ...(fiveWhyAnalisys.five_why_analysis_id && {
                                        five_why_analysis_id: fiveWhyAnalisys.five_why_analysis_id,
                                    }),
                                    ...why,
                                })),
                            },
                        });
                    }
                }
                await refetch();
                await refetchLogs();
                setIsSaving(false);
                setSnackbarMessage(t("rca.rca_library.rca_detail.update_success"));
            } else {
                await upsertSetRCA5WhyAnalysis({
                    variables: {
                        rca: {
                            rca_id: rca.rca_id,
                        },
                        five_why_analysis: {
                            ...(fiveWhyAnalisys.five_why_analysis_id && {
                                five_why_analysis_id: fiveWhyAnalisys.five_why_analysis_id,
                            }),
                            rca_id: rca.rca_id,
                            problem_statement: fiveWhyAnalisys.problem_statement,
                            root_cause_conclusion: fiveWhyAnalisys.root_cause_conclusion,
                        },
                        why: (fiveWhyAnalisys.why || []).map((why) => ({
                            ...(why.why_id && { why_id: why.why_id }),
                            ...why,
                        })),
                    },
                });
                refetch();
                await refetchLogs();
                setIsSaving(false);
                setSnackbarMessage(t("rca.rca_library.rca_detail.update_success"));
            }
        } catch (error) {
            setIsSaving(false);
            return;
        }
    };

    const handleFiveWhysAnalysisItemSave = async (why: Partial<IWhy>) => {
        if (!why.five_why_analysis_id) {
            setIsSaving(true);

            try {
                const fiveWhyAnalysisId = await upsertSetRCA5WhyAnalysis({
                    variables: {
                        rca: {
                            rca_id: rca.rca_id,
                        },
                        rca_id: rca.rca_id,
                        problem_statement: "",
                        root_cause_conclusion: "",
                    },
                });

                if (fiveWhyAnalysisId) {
                }
            } catch {
                setIsSaving(false);
                return;
            }
        }
    };

    const handleFiveWhyMediaDelete = async (mediaId: number, fiveWhyAnalysisId: number) => {
        try {
            await updateFiveWhyAnalysisMedia({
                variables: {
                    media_id: mediaId,
                    five_why_analysis_id: fiveWhyAnalysisId,
                },
            });
            await refetch();
            await refetchLogs();
            setSnackbarMessage(t("rca.rca_library.rca_detail.media.delete_success"));
        } catch {
            setSnackbarFailureMessage(t("rca.rca_library.rca_detail.media.delete_failed"));
            return;
        }
    };

    const handleDeleteMediaFile = async (rcaMediaId: number) => {
        const rcaMediaValues = {
            deleted_at: moment.utc().toDate(),
        };

        try {
            await updateRCAMediaMutation({
                variables: {
                    rcaMediaId: rcaMediaId,
                    changes: {
                        ...rcaMediaValues,
                    },
                },
            });
            await refetch();
            await refetchLogs();

            setSnackbarMessage(t("rca.rca_library.rca_detail.media.delete_success"));
        } catch {
            setSnackbarFailureMessage(t("rca.rca_library.rca_detail.media.delete_failed"));
            return;
        }
    };

    const handleDeleteRCA = async () => {
        setIsSaving(true);
        const rcaDetailValues = {
            deleted_at: moment.utc().toDate(),
        };
        const state = location.state;

        try {
            await upsertSetRCAWithTeamMembers({
                variables: {
                    rcaId: rca.rca_id,
                    rcaChanges: rcaDetailValues,
                    rca_team_members: [],
                },
            });

            if (state?.from) {
                navigate(state?.from);
            } else {
                navigate(
                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}`,
                );
            }
            setIsSaving(false);
        } catch {
            setSnackbarFailureMessage(t("rca.rca_library.rca_detail.delete_failed"));
            setIsSaving(false);
            return;
        }
    };

    const handleRefetchOnFishboneDiagram = async () => {
        await refetch();
        await refetchLogs();
    };

    useEffect(() => {
        const isComingFromActionPlan = location.state?.from?.includes(
            `${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}`,
        );

        if (
            isComingFromActionPlan &&
            !loading &&
            rca &&
            actionPlansTableRef.current &&
            !hasScrolledToActionPlansRef.current
        ) {
            hasScrolledToActionPlansRef.current = true;
            setTimeout(() => {
                actionPlansTableRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 10);
        }
    }, [loading, rca, location.state]);

    if ((!rca && !loading) || (rca && !rca.rca_id))
        return (
            <div className={mainClasses.mainWrapper}>
                <div className={`${classes.issueDetailWrapper} ${classes.rcaDetailWrapperNoData}`}>
                    <CircularProgress color="secondary" />
                    {t("rca.rca_library.rca_detail.loading")}
                </div>
            </div>
        );

    if (!rcaId || !rca)
        return (
            <div className={mainClasses.mainWrapper}>
                <div className={`${classes.issueDetailWrapper} ${classes.rcaDetailWrapperNoData}`}>
                    <ErrorOutlineOutlinedIcon className={classes.noDataIcon} />
                    {t("rca.rca_library.rca_detail.no_data")}
                </div>
            </div>
        );

    return (
        <div className={mainClasses.mainWrapper}>
            <div className={classes.issueDetailWrapper}>
                <div className={`${classes.leftContainer} ${mainClasses.contentWrapper}`}>
                    <MainTransparentBackButton
                        onClick={() => {
                            navigate(
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}`,
                            );
                        }}
                    />
                    <div className={classes.header}>
                        <div className={classes.headerMainInfo}>
                            <Box className={mainClasses.contentFormBox}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_detail.rca_number")}
                                    </Typography>
                                </InputLabel>
                                <div className={classes.contentFormLabelBig}>{`${t("rca.rca")}-${
                                    rca.display_number
                                }`}</div>
                            </Box>
                            <Box className={mainClasses.contentFormBox}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_detail.incident_date")}
                                    </Typography>
                                </InputLabel>
                                <div className={classes.contentFormLabelBig}>
                                    {isRCAManual
                                        ? formatDate(rca?.incident_date || rca.created_at)
                                        : formatDate(rca.rca_event_reports[0]?.event_report.start_date)}
                                </div>
                            </Box>
                            <Box className={mainClasses.contentFormBox}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_detail.machine_center")}
                                    </Typography>
                                </InputLabel>
                                <div className={classes.contentFormLabelBig}>
                                    {Boolean(rca.rca_event_reports[0]?.event_report)
                                        ? rca.rca_event_reports[0]?.event_report?.machine_center?.short_name ||
                                          rca.rca_event_reports[0]?.event_report?.machine_center.name
                                        : rca?.rca_detail?.machine_center?.short_name ||
                                          rca?.rca_detail?.machine_center?.name ||
                                          "--"}
                                </div>
                            </Box>
                        </div>
                        {/* Elipsis menu hidden until implementation */}
                        <div className={classes.headerActions}>
                            <RCAElipsisMenu options={rcaElipsisMenuOptions} />
                        </div>
                    </div>
                    <Divider className={classes.divider} />
                    <div className={classes.rcaDetails}>
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.production_line")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={
                                    Boolean(rca.rca_event_reports[0]?.event_report)
                                        ? rca.rca_event_reports[0]?.event_report?.machine_center.production_line.name
                                        : rca?.rca_detail?.production_line?.name || "--"
                                }
                            />
                        </Box>
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.functional_location")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={
                                    functionalLocationNames.length
                                        ? functionalLocationNames.join(", ")
                                        : rca?.rca_detail?.functional_location
                                        ? handleNameTranslation(
                                              i18n.language,
                                              rca?.rca_detail?.functional_location.translation.translation_names,
                                          )
                                        : "--"
                                }
                            />
                        </Box>
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.failure_type")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={
                                    translateDefaultEntity(
                                        i18n.language,
                                        rca?.rca_setting?.failure_type as unknown as IFailureType,
                                        "name",
                                        DefaultTranslationFieldsInTable.FAILURE_TYPE,
                                    ) ||
                                    translateDefaultEntity(
                                        i18n.language,
                                        rca?.rca_detail?.failure_type as unknown as IFailureType,
                                        "name",
                                        DefaultTranslationFieldsInTable.FAILURE_TYPE,
                                    ) ||
                                    "--"
                                }
                            />
                        </Box>
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.failure_code")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={
                                    failureCodeNames.length
                                        ? failureCodeNames.join(", ")
                                        : translateDefaultEntity(
                                              i18n.language,
                                              rca?.rca_detail?.failure_code as unknown as IFailureCode,
                                              "name",
                                              DefaultTranslationFieldsInTable.FAILURE_CODE,
                                          ) || "--"
                                }
                            />
                        </Box>
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.secondary_failure_code")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={
                                    secondaryFailureCodeNames.length
                                        ? secondaryFailureCodeNames.join(", ")
                                        : translateDefaultEntity(
                                              i18n.language,
                                              rca?.rca_detail?.secondary_failure_code as unknown as IFailureCode,
                                              "name",
                                              DefaultTranslationFieldsInTable.FAILURE_CODE,
                                          ) || "--"
                                }
                            />
                        </Box>
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.shift")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={shiftNames.length ? shiftNames.join(", ") : rca?.rca_detail?.shift?.name || "--"}
                            />
                        </Box>
                        {!isRCAManual && (
                            <Box className={mainClasses.contentFormBox}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_detail.documented_by")}
                                    </Typography>
                                </InputLabel>
                                <TextField
                                    disabled
                                    fullWidth
                                    className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                    multiline
                                    rows={2}
                                    type="text"
                                    variant="outlined"
                                    value={
                                        Boolean(rca.rca_event_reports[0]?.event_report?.user_operator)
                                            ? `${rca.rca_event_reports[0]?.event_report?.user_operator.first_name} ${rca.rca_event_reports[0]?.event_report?.user_operator.last_name}`
                                            : "--"
                                    }
                                />
                            </Box>
                        )}
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.rca_type")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={rca.rca_type.name}
                            />
                        </Box>
                        {/*
                         * These properties have been commented out as they currently don't have
                         * a way of being populated in the RCA (Root Cause Analysis) flow. They may be
                         * reintroduced or repurposed in future iterations of the code.
                         */}
                        {/* <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.key_success_factor")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={
                                    translateDefaultEntity(
                                        i18n.language,
                                        rca.key_success_factor,
                                        "name",
                                        DefaultTranslationFieldsInTable.KEY_SUCCESS_FACTOR,
                                    ) || "--"
                                }
                            />
                        </Box> */}
                        {/* <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.key_performance_indicator")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={rca?.key_performance_indicator?.name || "--"}
                            />
                        </Box> */}
                        {!isRCAManual && (
                            <Box className={mainClasses.contentFormBox}>
                                <InputLabel>
                                    <Typography
                                        style={{ display: "flex", alignItems: "center" }}
                                        variant="subtitle2"
                                        className={mainClasses.contentFormLabel}
                                    >
                                        {t("rca.rca_library.rca_detail.downtime_length")}{" "}
                                        <FrequencyCumulativeLengthTooltip
                                            lastSevenDaysDowntime={lastSevenDaysDowntime}
                                        />
                                    </Typography>
                                </InputLabel>
                                <TextField
                                    disabled
                                    fullWidth
                                    className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                    multiline
                                    rows={2}
                                    type="text"
                                    variant="outlined"
                                    value={rcaLength}
                                />
                            </Box>
                        )}
                        {/* <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.corrective_work_order_number")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={2}
                                type="text"
                                variant="outlined"
                                value={rca.corrective_work_order_number || "--"}
                            />
                        </Box> */}
                    </div>
                    {!isRCAManual && (
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.operator_notes")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={4}
                                type="text"
                                variant="outlined"
                                value={rca.rca_event_reports[0]?.event_report?.notes || "--"}
                            />
                        </Box>
                    )}
                    <Box className={mainClasses.contentFormBox}>
                        <InputLabel id="outlined-rcaDescription-label">
                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                {t("rca.rca_library.rca_detail.description")}
                            </Typography>
                        </InputLabel>
                        <TextField
                            disabled
                            fullWidth
                            className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                            multiline
                            rows={4}
                            type="text"
                            variant="outlined"
                            value={rca.incident_description || "--"}
                        />
                    </Box>
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.rca_library.rca_detail.observations.title")}
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                                    title={t("rca.rca_library.rca_detail.observations.add_new_observation")}
                                    renderIcon={() => <AddCircleIcon />}
                                    onClick={() => setOpenRCAObservationModal(true)}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContentObservations}>
                            {rca.rca_observations.map((observation) => (
                                <RCAObservationItem observation={observation} key={observation.rca_observation_id} />
                            ))}
                        </div>
                    </Card>

                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.rca_library.rca_detail.documents.title")}
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                                    title={t("rca.rca_library.rca_detail.documents.upload_a_file")}
                                    renderIcon={() => <FileUploadOutlinedIcon />}
                                    onClick={() => dropzoneDocumentsRef.current?.open()}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {!!RCA_PERMISSIONS?.rca_library.canEdit && (
                                <UploadDropZone
                                    onFilesChange={(files) => handleFileChange(files, RCAUploadTypeEnum.DOCUMENT)}
                                    hidePreview
                                    progress={progress}
                                    dropzoneRef={dropzoneDocumentsRef}
                                    showAddedFiles={false}
                                    renderUploadZone={() => <RCAUploadZone uploadType={RCAUploadTypeEnum.DOCUMENT} />}
                                    acceptedFileTypes={documentsFileTypeExtensions}
                                />
                            )}
                            {isUploadingFiles === RCAUploadTypeEnum.DOCUMENT && (
                                <div className={classes.progressWrapper}>
                                    <div>{t("rca.rca_library.rca_detail.documents.uploading")}</div>
                                    <LinearProgress variant="determinate" value={progress} color="info" />
                                </div>
                            )}
                            {!!documentFiles && documentFiles.length > 0 && (
                                <div className={classes.documentFilesWrapper}>
                                    {documentFiles.map((documentFile) => (
                                        <DocumentPreviewer
                                            document={documentFile}
                                            key={documentFile.rca_media_id}
                                            canDelete={RCA_PERMISSIONS?.rca_library?.canDelete}
                                            handleDelete={async () => {
                                                await handleDeleteMediaFile(documentFile?.rca_media_id);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.rca_library.rca_detail.media.title")}
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                                    title={t("rca.rca_library.rca_detail.media.upload_a_photo_or_video")}
                                    renderIcon={() => <FileUploadOutlinedIcon />}
                                    onClick={() => dropzoneMediaRef.current?.open()}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {!!RCA_PERMISSIONS?.rca_library.canEdit && (
                                <UploadDropZone
                                    hide={!!rca.rca_media && rca.rca_media.length > 0}
                                    dropzoneRef={dropzoneMediaRef}
                                    onFilesChange={(files) => handleFileChange(files, RCAUploadTypeEnum.MEDIA)}
                                    hidePreview
                                    showAddedFiles={false}
                                    renderUploadZone={() => <RCAUploadZone uploadType={RCAUploadTypeEnum.MEDIA} />}
                                    acceptedFileTypes={mediaFileTypeExtensions}
                                />
                            )}
                            {isUploadingFiles === RCAUploadTypeEnum.MEDIA && (
                                <div className={classes.progressWrapper}>
                                    <div>{t("rca.rca_library.rca_detail.media.uploading")}</div>
                                    <LinearProgress variant="determinate" value={progress} color="info" />
                                </div>
                            )}
                            {!!mediaFiles && mediaFiles.length > 0 && (
                                <div className={classes.mediaFilesWrapper}>
                                    {mediaFiles.map((mediaFile) => (
                                        <RCAMediaPreviewer
                                            canDelete={RCA_PERMISSIONS?.rca_library?.canDelete}
                                            mediaFile={mediaFile.media}
                                            key={mediaFile.rca_media_id}
                                            handleDelete={async () => {
                                                await handleDeleteMediaFile(mediaFile?.rca_media_id);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>

                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.rca_library.rca_detail.5_whys_analysis.title")}
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                {rca.five_why_analyses &&
                                    rca.five_why_analyses[0] &&
                                    !rca.five_why_analyses[0].media && (
                                        <MainCardButtonAction
                                            disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                                            title={t("rca.rca_library.rca_detail.documents.upload_a_file")}
                                            renderIcon={() => <FileUploadOutlinedIcon />}
                                            onClick={() => dropzoneFiveWhyAnalysisRef.current?.open()}
                                        />
                                    )}
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {!!RCA_PERMISSIONS?.rca_library.canEdit && (
                                <UploadDropZone
                                    onFilesChange={(files) =>
                                        handleFileFiveWhyAnalysisChange(files, RCAUploadTypeEnum.ALL)
                                    }
                                    hidePreview
                                    progress={progress}
                                    maxFiles={1}
                                    dropzoneRef={dropzoneFiveWhyAnalysisRef}
                                    showAddedFiles={false}
                                    renderUploadZone={() => <RCAUploadZone uploadType={RCAUploadTypeEnum.ALL} />}
                                    acceptedFileTypes={allFileTypeExtensions}
                                />
                            )}
                            {isUploadingFiles === RCAUploadTypeEnum.ALL && (
                                <div className={classes.progressWrapper}>
                                    <div>{t("rca.rca_library.rca_detail.documents.uploading")}</div>
                                    <LinearProgress variant="determinate" value={progress} color="info" />
                                </div>
                            )}
                            {rca.five_why_analyses &&
                                rca.five_why_analyses[0] &&
                                rca.five_why_analyses[0].media &&
                                documentsFileTypeExtensionsRegExp.test(rca.five_why_analyses[0].media.media_type) && (
                                    <div className={classes.fiveWhyAnalysisDocumentFilesWrapper}>
                                        <DocumentPreviewer
                                            document={rca?.five_why_analyses[0]}
                                            canDelete={RCA_PERMISSIONS?.rca_library?.canDelete}
                                            handleDelete={async () => {
                                                const { media, five_why_analysis_id } = rca?.five_why_analyses[0];
                                                media?.media_id &&
                                                    (await handleFiveWhyMediaDelete(
                                                        media.media_id,
                                                        five_why_analysis_id,
                                                    ));
                                            }}
                                        />
                                    </div>
                                )}
                            {rca.five_why_analyses &&
                                rca.five_why_analyses[0] &&
                                rca.five_why_analyses[0].media &&
                                mediaFileTypeExtensionsRegExp.test(rca.five_why_analyses[0].media.media_type) && (
                                    <div className={classes.mediaFilesFiveWhyAnalysisWrapper}>
                                        <RCAMediaPreviewer
                                            canDelete={RCA_PERMISSIONS?.rca_library?.canDelete}
                                            mediaFile={rca.five_why_analyses[0]?.media}
                                            handleDelete={async () => {
                                                const { media, five_why_analysis_id } = rca?.five_why_analyses[0];
                                                media?.media_id &&
                                                    (await handleFiveWhyMediaDelete(
                                                        media.media_id,
                                                        five_why_analysis_id,
                                                    ));
                                            }}
                                        />
                                    </div>
                                )}
                            {((rca.five_why_analyses && rca.five_why_analyses[0]) ||
                                (rca.five_why_analyses && !rca.five_why_analyses.length)) && (
                                <RCA5WhysAnalisys
                                    rca={rca}
                                    onSave={handleFiveWhysAnalysisSave}
                                    onSaveItem={handleFiveWhysAnalysisItemSave}
                                />
                            )}
                        </div>
                    </Card>

                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.rca_library.rca_detail.fishbone_diagram.title")}
                            </div>
                        </div>
                        <div className={classes.cardContentFishboneDiagram}>
                            <RCAFisboneDiagram
                                fishboneDiagram={rca.fishbone_diagram[0]}
                                rca={rca}
                                onSaveFishboneDiagram={handleRefetchOnFishboneDiagram}
                            />
                        </div>
                    </Card>

                    <ActionPlansTable
                        ref={actionPlansTableRef}
                        parentType="rca"
                        parentId={rca.rca_id}
                        rows={!loading && !apolloQueryError ? rca.action_plans || [] : []}
                        loading={loading}
                        millId={rca?.mill_id}
                        onActionPlanCreated={async () => {
                            await refetch();
                            await refetchLogs();
                        }}
                    />

                    <RCALibraryHistoryLogs logs={rcaLogs} loadingLogs={loadingLogs} />
                </div>

                <div className={classes.rightContainer}>
                    <Formik
                        initialValues={initFormValues}
                        validationSchema={addRCALibraryFormValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue, dirty }) => {
                            return (
                                <Form className={mainClasses.contentForm}>
                                    <RCAStatusDropdown
                                        disabled={isSaving || !RCA_PERMISSIONS?.rca_library.canEdit}
                                        handleChange={(value) => {
                                            setFieldValue("statusId", value);
                                        }}
                                        value={values.statusId}
                                    />
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel>
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.rca_library.rca_detail.due_date")}
                                            </Typography>
                                        </InputLabel>

                                        <DatePicker
                                            disablePast
                                            className={`${mainClasses.contentFormDatePicker} ${classes.datePicker}`}
                                            value={dayjs(values.dueDate)}
                                            format={getDateFormatByLanguage()}
                                            disabled={isSaving || !RCA_PERMISSIONS?.rca_library.canEdit}
                                            onChange={(date) => {
                                                setFieldValue("dueDate", date);
                                            }}
                                        />
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel>
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.rca_library.rca_detail.owner")}
                                            </Typography>
                                        </InputLabel>
                                        <RCAUsersDropdown
                                            fullWidth
                                            name="ownerId"
                                            millId={rca?.mill_id}
                                            disabled={isSaving || !RCA_PERMISSIONS?.rca_library.canEdit}
                                            placeholder={t("rca.settings.owners_dropdown.select_owner")}
                                            value={values.ownerId}
                                            onChange={(selected) => {
                                                setFieldValue("ownerId", selected);
                                            }}
                                            multiple={false}
                                        />
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-meetingTypeId-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.rca_library.rca_detail.meeting_type")}
                                            </Typography>
                                        </InputLabel>
                                        <RCAMeetingTypesDropdown
                                            fullWidth
                                            multiple={false}
                                            disabled={isSaving || !RCA_PERMISSIONS?.rca_library.canEdit}
                                            value={values.meetingTypeId}
                                            onChange={(value) => {
                                                setFieldValue("meetingTypeId", value);
                                            }}
                                        />
                                    </Box>
                                    {rca.created_by && (
                                        <>
                                            <Divider className={classes.formItemDivider} />
                                            <Box className={mainClasses.contentFormBox}>
                                                <InputLabel id="outlined-owner-label">
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={mainClasses.contentFormLabel}
                                                    >
                                                        {t("rca.action_plans.action_plan_details.submitted_by")}
                                                    </Typography>
                                                </InputLabel>
                                                <RCAUsersDropdown
                                                    millId={rca.mill_id}
                                                    fullWidth
                                                    disabled
                                                    name="submittedById"
                                                    placeholder={t(
                                                        "rca.action_plans.action_plan_details.select_submitted_by",
                                                    )}
                                                    value={rca.created_by}
                                                    onChange={(selected) => {}}
                                                    multiple={false}
                                                    className={classes.disabledDropdown}
                                                />
                                            </Box>
                                        </>
                                    )}
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel className={classes.labelWithAction}>
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.rca_library.rca_detail.rca_team_members")}
                                            </Typography>
                                            <Button
                                                variant="text"
                                                color="info"
                                                className={classes.labelButton}
                                                size="small"
                                                disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                                                onClick={() => setOpenRCAMembersModal(true)}
                                            >
                                                <AddCircleIcon />
                                            </Button>
                                        </InputLabel>
                                        <div className={classes.rcaMembersWrapper}>
                                            {rcaMembersListToShow.map((member, index) => {
                                                const isUserInSelected = Boolean(
                                                    selectedRCATeamMembers.find(
                                                        (user) => user.user_id === member.user.user_id,
                                                    ),
                                                );
                                                return (
                                                    <div className={classes.rcaMembersItemWrapper} key={index}>
                                                        <RCAUserWithAvatar
                                                            name={`${member.user.first_name} ${member.user.last_name}`}
                                                        />
                                                        {!!isUserInSelected && (
                                                            <IconButton
                                                                onClick={() => {
                                                                    setSelectedRCATeamMembers(
                                                                        selectedRCATeamMembers.filter(
                                                                            (user) =>
                                                                                user.user_id !== member.user.user_id,
                                                                        ),
                                                                    );
                                                                    setRcaMainDataChanged(true);
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {openRCAMembersModal && (
                                            <AddRCAMembersModal
                                                open
                                                millId={rca?.mill_id}
                                                onClose={() => setOpenRCAMembersModal(false)}
                                                currentRCATeamMembers={rcaMembersListToShow}
                                                onSaveRCATeamMembers={(userList) => {
                                                    setSelectedRCATeamMembers([...selectedRCATeamMembers, ...userList]);
                                                    setRcaMainDataChanged(true);
                                                    setOpenRCAMembersModal(false);
                                                }}
                                            />
                                        )}
                                        {openRCAObservationModal && (
                                            <RCAAddObservationModal
                                                open
                                                onClose={() => setOpenRCAObservationModal(false)}
                                                onAddObservation={async () => await refetch()}
                                                id={rca.rca_id}
                                                rca
                                            />
                                        )}
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    <Button
                                        className={mainClasses.contentButton}
                                        variant="contained"
                                        color="info"
                                        type="submit"
                                        disabled={
                                            isSaving ||
                                            (!dirty && !rcaMainDataChanged) ||
                                            !RCA_PERMISSIONS?.rca_library.canEdit
                                        }
                                    >
                                        {t("rca.rca_library.rca_detail.save_changes")}
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                    <Snackbar
                        open={snackbarMessage !== ""}
                        onClose={() => setSnackbarMessage("")}
                        autoHideDuration={6000}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Alert severity="success" sx={{ width: "100%" }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                    <Snackbar
                        open={snackbarFailureMessage !== ""}
                        onClose={() => setSnackbarFailureMessage("")}
                        autoHideDuration={6000}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Alert severity="error" sx={{ width: "100%" }}>
                            {snackbarFailureMessage}
                        </Alert>
                    </Snackbar>
                    <RCAConfirmDeleteModal
                        open={openRCADeleteModal}
                        onClose={() => setOpenRCADeleteModal(false)}
                        onDeleteItem={handleDeleteRCA}
                        title={t("rca.rca_library.rca_detail.rca_delete_modal.title")}
                        warning={t("rca.rca_library.rca_detail.rca_delete_modal.warning")}
                        confirm={t("rca.rca_library.rca_detail.rca_delete_modal.confirm")}
                    />
                    <CreateEditManualRCAModal
                        open={isCreateRCAModalOpen}
                        onClose={() => setIsCreateRCAModalOpen(false)}
                        rcaEditionValues={rca}
                        onAddRCA={async () => {
                            refetch();
                            await refetchLogs();
                            setSnackbarMessage(t("rca.rca_library.rca_detail.update_success"));
                            setIsCreateRCAModalOpen(false);
                        }}
                    />
                    <MainModal
                        isOpen={isFileUploadModalOpen}
                        title={t("upload_files_description_modal.title")}
                        onClose={() => setIsFileUploadModalOpen(false)}
                        onOkButtonClick={() => {
                            setIsFileUploadModalOpen(false);
                            handleFileUpload();
                        }}
                    >
                        <TextField
                            className={mainClasses.contentFormTextFieldModal}
                            type="text"
                            variant="outlined"
                            placeholder={t("upload_files_description_modal.text_field_placeholder")}
                            value={fileDescription}
                            onChange={(e) => setFileDescription(e.target.value)}
                        />
                    </MainModal>
                    <MainModal
                        isOpen={isFiveWhysFileUploadModalOpen}
                        title={t("upload_files_description_modal.title")}
                        onClose={() => setIsFiveWhysFileUploadModalOpen(false)}
                        onOkButtonClick={() => {
                            setIsFiveWhysFileUploadModalOpen(false);
                            handle5WhysFileUpload();
                        }}
                    >
                        <TextField
                            className={mainClasses.contentFormTextFieldModal}
                            type="text"
                            variant="outlined"
                            placeholder={t("upload_files_description_modal.text_field_placeholder")}
                            value={fiveWhysFileDescription}
                            onChange={(e) => setFiveWhysFileDescription(e.target.value)}
                        />
                    </MainModal>
                </div>
            </div>
        </div>
    );
};

export default RCALibraryDetail;
